/**
 * Letter Throwing Animation - Web Implementation (Orchestrator)
 *
 * Variant router that delegates to extracted animation components.
 * Each variant lives in its own file for <300L compliance.
 *
 * Supports animation variants (A/B testing):
 * 1. 'typewriter' (default): Simple text reveal with cursor
 * 2. 'letterThrowing': Full letter throwing with chromatic layers
 * 3. 'wordConvergence': Letters scatter then converge into words
 * 4. 'fullExperience': Complete 5-phase animation
 * 5. 'letterZoom': Semantic zoom with theatrical mode
 *
 * All variants include 5-phase system:
 * - EMERGENCE (0-55%): Text animation
 * - TRANSITION (55-60%): Brief pause
 * - READING (60-82%): Full text displayed
 * - CONVERGENCE (82-90%): Shrink, center, clear text
 * - REVELATION (90-100%): Text dims to backdrop, badge reveals with delay
 */

import type { LetterThrowingAnimationProps } from './types';
import TypewriterAnimation from './TypewriterAnimation.web';
import LetterThrowingFullAnimation from './LetterThrowingFullAnimation.web';
import WordConvergenceAnimation from './WordConvergenceAnimation.web';
import FullExperienceAnimation from './FullExperienceAnimation.web';
import { LetterZoomAnimation } from './letterZoom';

export default function LetterThrowingAnimation(props: LetterThrowingAnimationProps) {
  const { variant = 'typewriter' } = props;

  if (variant === 'letterZoom') {
    return <LetterZoomAnimation {...props} />;
  }

  if (variant === 'fullExperience') {
    return <FullExperienceAnimation {...props} />;
  }

  if (variant === 'letterThrowing') {
    return <LetterThrowingFullAnimation {...props} />;
  }

  if (variant === 'wordConvergence') {
    return <WordConvergenceAnimation {...props} />;
  }

  return <TypewriterAnimation {...props} />;
}
